import api from './api'
import { toCamelCase } from '@/utils/transform'

export interface BrokerFirm {
  id: string
  name: string
  normalizedName: string
  emailDomains: string[]
  city?: string | null
  state?: string | null
  website?: string | null
  phone?: string | null
  isActive: boolean
  needsReview: boolean
  contactCount: number
}

export interface BrokerContact {
  id: string
  name: string
  firm: string
  firmId?: string | null
  email?: string | null
  /** Other addresses the contact is known by (merged duplicates, aliases). */
  emails?: string[] | null
  phone?: string | null
  title?: string | null
  relationshipStatus?: string | null
  needsReview?: boolean
}

export interface BrokerHistoryItem {
  kind: 'message' | 'submission' | 'deal'
  id: string
  occurredAt?: string | null
  property?: string | null
  subject?: string | null
  sourceEmail?: string | null
  sourceType?: string | null
  threadGroupId?: string | null
  status?: string | null
  stage?: string | null
  killReason?: string | null
  linkedLoanId?: string | null
  linkedIngestionId?: string | null
  archived: boolean
}

export interface BrokerHistoryPage {
  brokerId: string
  requestedBrokerId: string
  mergedContactIds: string[]
  page: number
  pageSize: number
  total: number
  pages: number
  items: BrokerHistoryItem[]
}

export interface BrokerImportRow {
  firm: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  contactTitle?: string
}

export interface BrokerImportReportRow {
  row: number
  firm?: string
  firmAction?: 'matched' | 'created' | 'held-near-duplicate'
  firmId?: string | null
  similarTo?: Array<{ id: string; name: string; score: number; source: string }>
  contact?: string
  contactAction?: 'matched' | 'created' | 'held-near-duplicate' | 'held-with-firm'
  contactSimilarTo?: Array<{ id: string; name: string; score: number; source: string }>
  status?: 'skipped'
  reason?: string
}

export interface BrokerImportPreview {
  committed: boolean
  firmsToCreate: number
  contactsToCreate: number
  heldForReview: number
  previewToken: string
  rows: BrokerImportReportRow[]
  note: string
}

export interface BrokerLinkPayload {
  brokerId?: string
  brokerFirmId?: string
  createFirm?: { name: string; emailDomain?: string; website?: string; phone?: string }
  createContact?: { name: string; email?: string; phone?: string; title?: string }
  forceNew?: boolean
}

function importRows(rows: BrokerImportRow[]) {
  return rows.map((row) => ({
    firm: row.firm,
    contact_name: row.contactName,
    contact_email: row.contactEmail,
    contact_phone: row.contactPhone,
    contact_title: row.contactTitle,
  }))
}

export const brokerDirectoryService = {
  listFirms: () =>
    api.get('/broker-firms/').then((response) => toCamelCase<BrokerFirm[]>(response.data)),

  listContacts: () =>
    api.get('/brokers/').then((response) => toCamelCase<BrokerContact[]>(response.data)),

  getContactHistory: (brokerId: string, page = 1, pageSize = 25) =>
    api
      .get(`/brokers/${brokerId}/history`, { params: { page, page_size: pageSize } })
      .then((response) => toCamelCase<BrokerHistoryPage>(response.data)),

  previewImport: (rows: BrokerImportRow[]) =>
    api
      .post('/broker-firms/bulk-import', { rows: importRows(rows) })
      .then((response) => toCamelCase<BrokerImportPreview>(response.data)),

  commitImport: (rows: BrokerImportRow[], previewToken: string) =>
    api
      .post('/broker-firms/bulk-import', {
        rows: importRows(rows),
        commit: true,
        preview_token: previewToken,
      })
      .then((response) => toCamelCase<BrokerImportPreview>(response.data)),

  /**
   * Add a brokerage to the CRM from a deal being keyed in by hand. The server
   * refuses an exact duplicate and, unless `force`, a near one (409 with the
   * candidates), exactly as intake's "Create new" does.
   */
  createFirm: (input: { name: string; emailDomain?: string; force?: boolean }) =>
    api
      .post('/broker-firms/', {
        name: input.name,
        email_domains: input.emailDomain ? [input.emailDomain] : [],
        force: input.force ?? false,
      })
      .then((response) => toCamelCase<BrokerFirm>(response.data)),

  /** Add a broker contact to the CRM, under a firm already in it. */
  createContact: (input: { name: string; firm: string; firmId?: string | null; email?: string }) =>
    api
      .post('/brokers/', {
        name: input.name,
        firm: input.firm,
        firm_id: input.firmId ?? undefined,
        email: input.email || undefined,
      })
      .then((response) => toCamelCase<BrokerContact>(response.data)),

  linkIngestion: (ingestionId: string, payload: BrokerLinkPayload) =>
    api
      .post(`/ingestion/${ingestionId}/broker-link`, {
        broker_id: payload.brokerId,
        broker_firm_id: payload.brokerFirmId,
        create_firm: payload.createFirm
          ? {
              name: payload.createFirm.name,
              email_domain: payload.createFirm.emailDomain,
              website: payload.createFirm.website,
              phone: payload.createFirm.phone,
            }
          : undefined,
        create_contact: payload.createContact
          ? {
              name: payload.createContact.name,
              email: payload.createContact.email,
              phone: payload.createContact.phone,
              title: payload.createContact.title,
            }
          : undefined,
        force_new: payload.forceNew ?? false,
      })
      .then((response) => toCamelCase<{
        ingestionId: string
        brokerId?: string | null
        brokerFirmId?: string | null
        loanId?: string | null
        created: Record<string, string>
      }>(response.data)),
}
